const { formatMoney, getElements } = window.BAO.Utils;

/**
 * @typedef {Object} SelectedVariantOption
 *
 * @property {string} label
 * @property {string} value
 */

const selectors = {
  addToCart: "[data-product-add-to-cart]",
  addToCartText: "[data-product-add-to-cart-text]",
  backInStockForm: "[data-product-back-in-stock-form]",
  backInStockToggle: "[data-product-back-in-stock-toggle]",
  backInStockProductTitle: "[data-product-back-in-stock-product-title]",
  form: "[data-product-form]",
  price: "[data-product-price]",
  priceValue: "[data-product-price-value]",
  priceCompare: "[data-product-price-compare]",
  priceCompareValue: "[data-product-price-compare-value]",
  priceCompareText: "[data-product-price-compare-text]",
  sku: "[data-product-sku]",
  id: "[data-product-id]",
};

class Variant {
  constructor(data) {
    /**
     * @type {ShopifyVariant}
     * @public
     */
    this.data = data;
  }

  setPrice() {
    unhidePriceEls();

    const priceElements = getElements(selectors.price);
    priceElements.forEach((el) => {
      el.classList.toggle(
        "prd-Pricing_Price-sale",
        !!this.data.compare_at_price,
      );
      el.classList.toggle("util-Color-red", !!this.data.compare_at_price);
    });

    const priceValueElements = getElements(selectors.priceValue);
    priceValueElements.forEach(
      (el) => (el.innerHTML = formatMoney(this.data.price)),
    );

    const priceCompareValueElements = getElements(selectors.priceCompareValue);
    priceCompareValueElements.forEach(
      (el) => (el.innerHTML = formatMoney(this.data.compare_at_price)),
    );

    if (!this.data.compare_at_price) {
      hidePriceCompareElements();
    }
  }

  setAvailability() {
    getElements(selectors.addToCart).forEach(
      (el) => (el.disabled = !this.data.available),
    );

    getElements(selectors.addToCartText).forEach(
      (el) =>
        (el.textContent = this.data.available
          ? window.theme.strings.addToCart
          : window.theme.strings.soldOut),
    );

    getElements(selectors.addToCart).forEach((el) => {
      el.setAttribute("aria-hidden", !this.data.available);
    });

    getElements(selectors.backInStockToggle).forEach((el) => {
      el.setAttribute("aria-hidden", this.data.available);
    });

    getElements(selectors.backInStockProductTitle).forEach((el) => {
      el.textContent = `${el?.dataset?.productTitle} - ${this.data.title}`;
    });

    getElements(selectors.backInStockForm).forEach((el) => {
      el.dataset.variantId = this.data.id;
    });

    getElements(selectors.sku).forEach((el) => {
      el.textContent = this.data.sku;
    });
  }

  updateInput() {
    const inputs = getElements(selectors.id);

    inputs.forEach((input) => {
      input.value = this.id;
      input.dispatchEvent(new Event("change", { bubbles: true }));
    });
  }

  setUrl() {
    window.history.replaceState(
      { path: this.url.toString() },
      "",
      this.url.toString(),
    );
  }

  setShareUrl() {
    const shareButtons = getElements("share-button");
    if (shareButtons.length === 0) return;

    shareButtons.forEach((shareButton) => {
      shareButton.updateUrl(this.url.toString());
    });
  }

  /**
   * @returns {number}
   */
  get id() {
    return this.data.id;
  }

  /**
   * @returns {URL}
   */
  get url() {
    // eslint-disable-next-line compat/compat
    const url = new URL(window.location.href);
    url.searchParams.set("variant", this.id.toString());

    return url;
  }
}

export class VariantOptions extends window.BAO.CustomElement() {
  static get requiredElements() {
    return ["data", "option", "option-choice"];
  }

  constructor() {
    super();

    /**
     * @type {Variant[]}
     * @public
     */
    this.collectedVariants = [new Variant(this.defaultVariant)];
  }

  setupListeners() {
    super.setupListeners();

    this.onVariantChange = this.onVariantChange.bind(this);

    this.listeners.add(this, "change", this.onVariantChange);
  }

  onVariantChange() {
    const variant = this.getVariant();

    variant.setAvailability();
    variant.setPrice();
    variant.updateInput();

    if (!this.currentVariant) {
      this.resetPage(variant);

      return;
    }

    variant.setUrl();
    variant.setShareUrl();
  }

  /**
   * @param {Variant} variant
   *
   * @returns {void}
   */
  resetPage(variant) {
    window.history.replaceState(
      { path: window.location.pathname },
      "",
      window.location.pathname,
    );
  }

  /**
   * @param {ShopifyVariant} data
   *
   * @returns {Variant}
   */
  createVariant(data) {
    const variant = new Variant(data);
    this.collectedVariants.push(variant);

    return variant;
  }

  /**
   * @returns {Variant}
   */
  getVariant() {
    if (!this.currentVariant) {
      return this.collectedVariants[0];
    }

    const existingVariant = this.collectedVariants.find(
      (variant) => variant.id === this?.currentVariant?.id,
    );
    if (existingVariant) return existingVariant;

    return this.createVariant(this.currentVariant);
  }

  /**
   * @returns {SelectedVariantOption[]}
   */
  get selectedOptions() {
    this.throwError("selectedOptions needs to be implemented on this class");

    return [];
  }

  /**
   * @returns {ShopifyVariant}
   */
  get defaultVariant() {
    return this.data[0];
  }

  /**
   * @returns {ShopifyVariant|undefined}
   */
  get currentVariant() {
    return this.data.find((variant) => {
      return variant.options.every(
        (option, index) => this.selectedOptions[index].value === option,
      );
    });
  }

  /**
   * @returns {ShopifyVariant[]}
   */
  get data() {
    return JSON.parse(this.els.data.element.innerText);
  }
}

export class VariantRadios extends VariantOptions {
  get selectedOptions() {
    return this.els.option.elements.map((el) => {
      const { name } = el.dataset;
      const choice = getElements(this.els["option-choice"].selector, {
        context: el,
      }).find((el) => el.checked).value;

      return {
        label: name,
        value: choice,
      };
    });
  }
}

export class VariantSelects extends VariantOptions {
  get selectedOptions() {
    return this.els.option.elements.map((el) => {
      const { name } = el.dataset;

      return {
        label: name,
        value: el.querySelector("select").value,
      };
    });
  }
}

if (!customElements.get("variant-radios")) {
  customElements.define("variant-radios", VariantRadios);
}

if (!customElements.get("variant-selects")) {
  customElements.define("variant-selects", VariantSelects);
}

function hideElements(elements) {
  elements.forEach((el) => (el.hidden = true));
}

function unhideElements(elements) {
  elements.forEach((el) => (el.hidden = false));
}

function unhidePriceEls() {
  unhideElements(getElements(`${selectors.price}, ${selectors.priceCompare}`));
}

function hidePriceCompareElements() {
  hideElements(getElements(selectors.priceCompare));
}
